import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { workAdjustmentApi, type WorkAdjustmentStatus } from "../lib/workAdjustmentApi";

const KEYS = {
  all: ["work-adjustments"] as const,
  mine: (businessId: string) => [...KEYS.all, "mine", businessId] as const,
  admin: (businessId: string, status: WorkAdjustmentStatus) =>
    [...KEYS.all, "admin", businessId, status] as const,
};

export function useMyWorkAdjustments(businessId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: KEYS.mine(businessId),
    queryFn: ({ pageParam }) =>
      workAdjustmentApi.listMine(businessId, { lastKey: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    enabled: enabled && !!businessId,
    staleTime: 0,
  });
}

export function useAdminWorkAdjustments(
  businessId: string,
  status: WorkAdjustmentStatus = "PENDING",
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: KEYS.admin(businessId, status),
    queryFn: ({ pageParam }) =>
      workAdjustmentApi.listAdmin(businessId, {
        status,
        lastKey: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    enabled: enabled && !!businessId,
    staleTime: 0,
  });
}

export function useSubmitWorkAdjustment(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      type: "OVERTIME" | "HOLIDAY_WORK" | "HOLIDAY_OVERTIME";
      workDate: string;
      overtimeHours?: number;
      description: string;
    }) => workAdjustmentApi.submit(businessId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.mine(businessId) }),
  });
}

export function useReviewWorkAdjustment(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      userId: string;
      requestId: string;
      action: "approve" | "reject";
      holidayType?: "REGULAR_HOLIDAY" | "SPECIAL_NON_WORKING";
      rejectionReason?: string;
    }) =>
      workAdjustmentApi.review(businessId, vars.userId, vars.requestId, {
        action: vars.action,
        holidayType: vars.holidayType,
        rejectionReason: vars.rejectionReason,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}
